import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Container, Row, Spinner, Alert, Button } from "react-bootstrap";
import API from "../../services/api";
import "../EmployeeAccounts.css";

const StatCard = ({ label, value, sub }) => (
  <div className="accounts-stat-card compact">
    <div className="accounts-stat-meta">
      <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: "22px", fontWeight: 800 }}>{value}</div>
      {sub ? <div style={{ fontSize: "11px", color: "#94a3b8" }}>{sub}</div> : null}
    </div>
  </div>
);

const HrDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState({});
  const [perms, setPerms] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setErr("");
      const me = await API.get("/auth/me");
      const p = me.data?.employeeRole?.permissions || [];
      const list = Array.isArray(p) ? p : [];
      setPerms(list);

      const can = (needed) => needed.some((perm) => list.includes(perm));
      const safeGet = async (endpoint, neededPerms) => {
        if (!can(neededPerms)) return { data: [] };
        try {
          return await API.get(endpoint);
        } catch (e) {
          if (e?.response?.status === 403) return { data: [] };
          throw e;
        }
      };

      const [
        employees,
        leave,
        attendance,
        performance,
        onboarding,
        offboarding,
        recruitment,
        training,
        grievance,
      ] = await Promise.all([
        safeGet("/employee/hr/employees", ["employees:view"]),
        safeGet("/employee/hr/leave-requests", ["leave:view", "leave:approve"]),
        safeGet("/employee/hr/attendance-approvals", ["attendance:view", "attendance:approve"]),
        safeGet("/employee/hr/performance-reviews", ["performance:view"]),
        safeGet("/employee/hr/onboarding", ["hr:onboarding:manage"]),
        safeGet("/employee/hr/offboarding", ["hr:offboarding:manage"]),
        safeGet("/employee/hr/recruitment", ["hr:recruitment:manage"]),
        safeGet("/employee/hr/training", ["hr:training:manage"]),
        safeGet("/employee/hr/grievance", ["hr:grievance:manage"]),
      ]);

      setData({
        employees: employees.data || [],
        leave: leave.data || [],
        attendance: attendance.data || [],
        performance: performance.data || [],
        onboarding: onboarding.data || [],
        offboarding: offboarding.data || [],
        recruitment: recruitment.data || [],
        training: training.data || [],
        grievance: grievance.data || [],
      });
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load HR dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const summary = useMemo(() => {
    const countByStatus = (rows, status) =>
      (rows || []).filter((r) => String(r.status).toLowerCase() === status).length;
    return {
      employees: (data.employees || []).length,
      leavePending: countByStatus(data.leave, "pending"),
      attendancePending: (data.attendance || []).filter(
        (r) => String(r.approvalStatus || r.status).toLowerCase() === "pending"
      ).length,
      onboardingActive: countByStatus(data.onboarding, "in-progress"),
      offboardingActive: countByStatus(data.offboarding, "in-progress"),
      recruitmentActive: (data.recruitment || []).filter((r) =>
        ["screening", "interview", "offer"].includes(String(r.status))
      ).length,
      trainingsActive: countByStatus(data.training, "ongoing"),
      grievanceOpen: (data.grievance || []).filter((r) =>
        ["open", "in-review"].includes(String(r.status))
      ).length,
    };
  }, [data]);

  const can = (needed) => needed.some((perm) => perms.includes(perm));
  const cards = [
    { key: "employees", label: "Total Employees", value: summary.employees, sub: "Active roster", perms: ["employees:view"] },
    { key: "leave", label: "Leave Pending", value: summary.leavePending, sub: "Awaiting approval", perms: ["leave:view", "leave:approve"] },
    { key: "attendance", label: "Attendance Pending", value: summary.attendancePending, sub: "Manual entries", perms: ["attendance:view", "attendance:approve"] },
    { key: "onboarding", label: "Onboarding Active", value: summary.onboardingActive, sub: "In progress", perms: ["hr:onboarding:manage"] },
    { key: "offboarding", label: "Offboarding Active", value: summary.offboardingActive, sub: "In progress", perms: ["hr:offboarding:manage"] },
    { key: "recruitment", label: "Recruitment Active", value: summary.recruitmentActive, sub: "Live pipeline", perms: ["hr:recruitment:manage"] },
    { key: "training", label: "Trainings Ongoing", value: summary.trainingsActive, sub: "Current sessions", perms: ["hr:training:manage"] },
    { key: "grievance", label: "Grievance Open", value: summary.grievanceOpen, sub: "Open cases", perms: ["hr:grievance:manage"] },
  ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Container className="mt-4 accounts-page">
      <div className="accounts-header">
        <div>
          <div className="accounts-title-row">
            <h4 className="mb-0">HR Dashboard</h4>
          </div>
          <p className="accounts-subtitle">People operations overview at a glance.</p>
        </div>
        <div className="accounts-actions">
          <Button variant="outline-secondary" size="sm" onClick={fetchData}>
            Refresh
          </Button>
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <Row className="g-3">
        {cards.filter((c) => can(c.perms)).map((c) => (
          <Col md={3} key={c.key}>
            <StatCard label={c.label} value={c.value} sub={c.sub} />
          </Col>
        ))}
        {cards.filter((c) => can(c.perms)).length === 0 && (
          <Col md={12}>
            <div className="accounts-list-card" style={{ padding: "16px" }}>
              No HR modules assigned to your role.
            </div>
          </Col>
        )}
      </Row>

      <Row className="g-3 mt-3">
        {can(["leave:view", "leave:approve"]) && (
          <Col md={6}>
            <Card className="accounts-list-card">
              <div className="accounts-list-head">
                <div className="accounts-list-title">Recent Leave Requests</div>
              </div>
              <div className="p-3" style={{ fontSize: "13px" }}>
                {(data.leave || []).slice(0, 5).map((row, idx) => (
                  <div key={row._id || idx} className="d-flex justify-content-between border-bottom py-2">
                    <div>{row.employeeName || "—"} · {row.leaveType || "—"}</div>
                    <div style={{ fontWeight: 700 }}>{row.status || "—"}</div>
                  </div>
                ))}
                {(data.leave || []).length === 0 && <div>No recent leave requests</div>}
              </div>
            </Card>
          </Col>
        )}
        {can(["hr:recruitment:manage"]) && (
          <Col md={6}>
            <Card className="accounts-list-card">
              <div className="accounts-list-head">
                <div className="accounts-list-title">Active Recruitment</div>
              </div>
              <div className="p-3" style={{ fontSize: "13px" }}>
                {(data.recruitment || []).slice(0, 5).map((row, idx) => (
                  <div key={row._id || idx} className="d-flex justify-content-between border-bottom py-2">
                    <div>{row.candidateName || "—"} · {row.role || "—"}</div>
                    <div style={{ fontWeight: 700 }}>{row.status || "—"}</div>
                  </div>
                ))}
                {(data.recruitment || []).length === 0 && <div>No active recruitment</div>}
              </div>
            </Card>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default HrDashboard;
